import React, { useState, useMemo } from 'react';
import { Form, Input, Button, Card, Typography, Layout, Radio, Row, Col, Space, Select, Grid } from 'antd';
import { MailOutlined, LockOutlined, UserOutlined, BankOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { appVersion } from '../../../config/config';
import { useNavigate, useLocation } from 'react-router-dom';
import { App as AntdApp } from 'antd';
import { useAuthApi } from '../../../api/auth/auth';
import { useUserApi } from '../../../api/user/userApp';
import { useReferential } from '../../../context/ReferentialContext';
import PersonalInfoForm from '../../../pages/configuration/users/userForm/PersonalInfoForm';
import BusinessInfoForm from '../../../pages/configuration/users/userForm/BusinessInfoForm';
import ContactsManager from '../../../pages/configuration/users/userForm/ContactsManager';
import LanguageSwitcher from '../../../components/auth/LanguageSwitcher';

const { Title, Text } = Typography;
const { Content, Footer } = Layout;

const buildRadioStyles = (primary: string, surface: string) => `
    .ant-radio-button-wrapper {
        border-radius: 12px !important;
        overflow: hidden;
    }
    .ant-radio-button-wrapper:first-child {
        border-radius: 12px !important;
    }
    .ant-radio-button-wrapper:last-child {
        border-radius: 12px !important;
    }
    .ant-radio-button-wrapper-checked {
        border-color: ${primary} !important;
        background: ${surface} !important;
        box-shadow: 0 0 0 3px ${primary}1a !important;
    }
    .ant-radio-button-wrapper-checked .anticon {
        color: ${primary} !important;
    }
    .ant-radio-button-wrapper-checked span:not(.anticon):not(.ant-radio-button) {
        color: ${primary} !important;
        font-weight: 600 !important;
    }
    .ant-radio-button-wrapper:hover:not(.ant-radio-button-wrapper-checked) {
        border-color: ${primary}88 !important;
    }
`;

type AccountType = 'parent' | 'organization';

interface RegisterFormValues {
    accountType: AccountType;
    login: string;
    email: string;
    password: string;
    confirmPassword: string;
    country?: string;
    personal_info?: {
        firstname: string;
        lastname: string;
        birthdate: string;
    };
    business_info?: {
        taxeregistry: string;
        name: string;
    };
    contacts?: Array<{
        type_id: number;
        contact_value: string;
    }>;
}

const Register: React.FC = () => {
    const { t } = useTranslation();
    const { register } = useAuthApi();
    const { generateLoginUserApp } = useUserApi();
    const { countries, isReferentialLoaded } = useReferential();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [generatingLogin, setGeneratingLogin] = useState<boolean>(false);
    const [form] = Form.useForm();
    const { message: antdMessage } = AntdApp.useApp();

    const [accountType, setAccountType] = useState<AccountType | null>(null);
    const [activeTab, setActiveTab] = useState<'login' | 'register'>(
        location.pathname === '/login' ? 'login' : 'register'
    );

    // Filter out Arabic characters (Unicode range \u0600-\u06FF)
    const filterArabicCharacters = (value: string): string => {
        return value.replace(/[\u0600-\u06FF]/g, '');
    };

    const showArabicError = () => {
        antdMessage.warning(t('auth.validation.noArabicCharacters'));
    };

    const palette = useMemo(
        () => ({
            primary: '#2D9CC9',
            secondary: '#8BBE1E',
            dark: '#031323',
            background: '#f8fafc',
            surface: '#ffffff',
            border: '#e2e8f0',
            textPrimary: '#0f172a',
            textSecondary: '#64748b',
            textMuted: '#94a3b8',
        }),
        // eslint-disable-next-line
        []
    );

    const radioStyles = useMemo(
        () => buildRadioStyles(palette.primary, palette.surface),
        [palette.primary, palette.surface]
    );

    const screens = Grid.useBreakpoint();
    const isCompact = screens.md !== true;

    const generateLogin = async () => {
        try {
            const values = await form.validateFields(['personal_info', 'business_info']);
            const firstname = values.personal_info?.firstname;
            const lastname = values.personal_info?.lastname;
            const businessname = values.business_info?.name;

            if (!businessname && (!firstname || !lastname)) {
                antdMessage.error(t('auth.register.nameRequired'));
                return;
            }
            if (firstname && lastname && (firstname.length < 2 || lastname.length < 2)) {
                antdMessage.error(t('auth.register.nameMinLength'));
                return;
            } else if (businessname && businessname.length < 2) {
                antdMessage.error(t('auth.register.businessNameMinLength'));
                return;
            }
            setGeneratingLogin(true);

            const payload = {
                firstname: firstname || 'school',
                lastname: lastname || businessname,
            };
            const response: any = await generateLoginUserApp(payload);
            const generatedLogin = response.data.login;
            form.setFieldsValue({ login: generatedLogin });
        } catch (err: any) {
            console.error('Error generating login:', err);
            antdMessage.error(err.response?.data ?? err.message ?? err);
        } finally {
            setGeneratingLogin(false);
        }
    };

    const onFinish = async (values: RegisterFormValues) => {
        if (isLoading) return;
        setIsLoading(true);

        try {
            // Validate password confirmation
            if (values.password !== values.confirmPassword) {
                antdMessage.error(t('auth.register.passwordMismatch'));
                setIsLoading(false);
                return;
            }

            // Convert YYYY-MM-DD from DatePickerHybrid to full ISO 8601 expected by the API
            let formattedPersonalInfo = values.personal_info;
            if (formattedPersonalInfo?.birthdate) {
                formattedPersonalInfo = {
                    ...formattedPersonalInfo,
                    birthdate: new Date(formattedPersonalInfo.birthdate + 'T00:00:00.000Z').toISOString(),
                };
            }

            // Prepare payload
            const payload: any = {
                login: values.login,
                email: values.email,
                password: values.password,
                creation_context: 'self_signup',
            };

            // Add metadata with selected country if provided
            if (values.country) {
                payload.metadata = { pays: values.country };
            }

            if (values.accountType === 'parent' && formattedPersonalInfo) {
                payload.personalInfo = formattedPersonalInfo;
            } else if (values.accountType === 'organization' && values.business_info) {
                payload.businessInfo = values.business_info;
            }

            // Add contacts if provided
            if (values.contacts && values.contacts.length > 0) {
                payload.contacts = values.contacts;
            }

            // Register user
            const result = await register(payload);
            if (result.email_sent === false) {
                antdMessage.warning(t('auth.register.emailNotSent', "Compte créé mais l'email d'activation n'a pas pu être envoyé. Contactez le support."));
            }

            antdMessage.success(t('auth.register.success'));
            navigate('/callback/email-confirm?status=pending');
        } catch (err: any) {
            antdMessage.error(
                err?.response?.data?.message ?? err?.message ?? t('auth.register.error')
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Layout
            style={{
                minHeight: '100vh',
                position: 'relative',
                background: palette.dark,
                padding: isCompact ? 'max(12px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right)) 24px max(12px, env(safe-area-inset-left))' : '40px',
                paddingTop: isCompact
                    ? 'max(52px, calc(env(safe-area-inset-top) + 40px))'
                    : undefined,
            }}
        >
            {/* Language Switcher - Top Right */}
            <div
                style={{
                    position: 'absolute',
                    top: isCompact ? 'max(12px, env(safe-area-inset-top))' : 40,
                    right: isCompact ? 'max(12px, env(safe-area-inset-right))' : 40,
                    zIndex: 1000,
                }}
            >
                <LanguageSwitcher variant="dark" />
            </div>

            <Content
                style={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: isCompact ? 'flex-start' : 'center',
                    position: 'relative',
                    maxWidth: '1200px',
                    width: '100%',
                    margin: '0 auto',
                    paddingTop: isCompact ? 8 : 0,
                }}
            >
                <div
                    style={{
                        width: '100%',
                        maxWidth: '1100px',
                        backgroundColor: palette.surface,
                        borderRadius: isCompact ? 16 : 24,
                        overflow: 'hidden',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                        border: `1px solid rgba(255, 255, 255, 0.1)`,
                        display: 'flex',
                        flexDirection: isCompact ? 'column' : 'row',
                        minHeight: isCompact ? undefined : 600,
                        maxHeight: isCompact ? undefined : '90vh',
                    }}
                >
                    {/* Left Column - Abstract Background */}
                    <div
                        style={{
                            width: isCompact ? '100%' : '40%',
                            flexShrink: 0,
                            minHeight: isCompact ? 200 : undefined,
                            position: 'relative',
                            background: `linear-gradient(135deg, 
                                rgba(45, 156, 201, 0.4) 0%, 
                                rgba(45, 156, 201, 0.6) 25%,
                                rgba(139, 190, 30, 0.5) 75%,
                                rgba(139, 190, 30, 0.3) 100%)`,
                            overflow: 'hidden',
                        }}
                    >
                        {/* Geometric Shapes Background */}
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                background: `
                                    polygon(0% 0%, 100% 0%, 85% 40%, 100% 70%, 60% 100%, 0% 80%, 20% 50%),
                                    polygon(40% 20%, 100% 10%, 95% 60%, 80% 90%, 20% 100%, 0% 70%, 10% 30%)
                                `,
                                backgroundSize: '200% 200%',
                                backgroundPosition: '0% 0%, 100% 100%',
                                mixBlendMode: 'multiply',
                                opacity: 0.3,
                            }}
                        />

                        {/* Overlay Geometric Patterns */}
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                background: `
                                    repeating-linear-gradient(45deg, 
                                        transparent, 
                                        transparent 20px, 
                                        rgba(255, 255, 255, 0.05) 20px, 
                                        rgba(255, 255, 255, 0.05) 40px)
                                `,
                                opacity: 0.5,
                            }}
                        />

                        {/* Content on Left Side */}
                        <div
                            style={{
                                position: 'relative',
                                zIndex: 2,
                                padding: isCompact ? '20px 16px 24px' : '48px 40px',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: isCompact ? 'flex-start' : 'space-between',
                                alignItems: 'center',
                                gap: isCompact ? 16 : 0,
                            }}
                        >
                            {/* Logo Section */}
                            <div style={{ width: '100%', textAlign: 'center', marginTop: isCompact ? 0 : 20 }}>
                                <img
                                    src="/images/logo_120x64.png"
                                    alt="Tilmicode"
                                    style={{
                                        height: isCompact ? 56 : 80,
                                        filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2))',
                                        marginBottom: '8px',
                                    }}
                                />
                            </div>

                            <div style={{ width: '100%', textAlign: 'center' }}>
                                {/* Login Tab Button */}
                                <div
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={activeTab === 'login'}
                                    onClick={() => { setActiveTab('login'); navigate('/login'); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setActiveTab('login'); navigate('/login'); } }}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 44,
                                        backgroundColor: activeTab === 'login' ? palette.surface : 'transparent',
                                        color: activeTab === 'login' ? palette.dark : palette.surface,
                                        padding: isCompact ? '10px 18px' : '12px 32px',
                                        borderRadius: '8px',
                                        fontSize: isCompact ? 14 : 16,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        marginBottom: '16px',
                                        transition: 'all 0.3s ease',
                                        border: `2px solid ${activeTab === 'login' ? palette.surface : 'transparent'}`,
                                        opacity: activeTab === 'login' ? 1 : 0.8,
                                        transform: activeTab === 'login' && !isCompact ? 'scale(1.05)' : 'scale(1)',
                                        outline: 'none',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (activeTab !== 'login') {
                                            e.currentTarget.style.opacity = '1';
                                            e.currentTarget.style.transform = isCompact ? 'scale(1)' : 'scale(1.02)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (activeTab !== 'login') {
                                            e.currentTarget.style.opacity = '0.8';
                                            e.currentTarget.style.transform = 'scale(1)';
                                        }
                                    }}
                                    onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,255,255,0.4)'; }}
                                    onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                                >
                                    LOGIN
                                </div>

                                {/* Sign In/Register Tab */}
                                <div
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={activeTab === 'register'}
                                    onClick={() => setActiveTab('register')}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveTab('register'); }}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 44,
                                        backgroundColor: activeTab === 'register' ? palette.surface : 'transparent',
                                        color: activeTab === 'register' ? palette.dark : palette.surface,
                                        padding: isCompact ? '10px 18px' : '12px 32px',
                                        borderRadius: '8px',
                                        fontSize: isCompact ? 14 : 16,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        border: `2px solid ${activeTab === 'register' ? palette.surface : 'transparent'}`,
                                        opacity: activeTab === 'register' ? 1 : 0.8,
                                        transform: activeTab === 'register' && !isCompact ? 'scale(1.05)' : 'scale(1)',
                                        outline: 'none',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (activeTab !== 'register') {
                                            e.currentTarget.style.opacity = '1';
                                            e.currentTarget.style.transform = isCompact ? 'scale(1)' : 'scale(1.02)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (activeTab !== 'register') {
                                            e.currentTarget.style.opacity = '0.8';
                                            e.currentTarget.style.transform = 'scale(1)';
                                        }
                                    }}
                                    onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,255,255,0.4)'; }}
                                    onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                                >
                                    SIGN IN
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Register Form */}
                    <div
                        style={{
                            width: isCompact ? '100%' : '60%',
                            flex: isCompact ? undefined : 1,
                            minWidth: 0,
                            backgroundColor: palette.surface,
                            padding: isCompact ? '24px 20px 28px' : '48px 60px',
                            display: 'flex',
                            flexDirection: 'column',
                            overflowY: isCompact ? 'visible' : 'auto',
                            maxHeight: isCompact ? 'none' : '90vh',
                        }}
                    >
                        {/* Header with Icon */}
                        <div style={{ textAlign: 'center', marginBottom: isCompact ? 24 : 32, flexShrink: 0 }}>
                            <div
                                style={{
                                    width: isCompact ? 64 : 80,
                                    height: isCompact ? 64 : 80,
                                    borderRadius: '50%',
                                    background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: isCompact ? 16 : 24,
                                    boxShadow: `0 8px 24px rgba(45, 156, 201, 0.3)`,
                                }}
                            >
                                <UserOutlined style={{ fontSize: isCompact ? 28 : 36, color: palette.surface }} />
                            </div>
                            <Title
                                level={1}
                                style={{
                                    margin: 0,
                                    color: palette.primary,
                                    fontSize: isCompact ? 22 : 32,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: isCompact ? 1 : 2,
                                    lineHeight: 1.2,
                                }}
                            >
                                {t('auth.register.title')}
                            </Title>
                            <Text
                                style={{
                                    color: palette.textSecondary,
                                    fontSize: '14px',
                                    marginTop: '8px',
                                    display: 'block',
                                }}
                            >
                                {t('auth.register.subtitle')}
                            </Text>
                        </div>

                        {/* Form Section - Scrollable */}
                        <div
                            style={{
                                flex: 1,
                                overflowY: isCompact ? 'visible' : 'auto',
                                paddingRight: isCompact ? 0 : 8,
                                minHeight: 0,
                            }}
                        >
                            <Form
                                form={form}
                                name="register_form"
                                onFinish={onFinish}
                                layout="vertical"
                                requiredMark={false}
                                initialValues={{ accountType: null }}
                            >
                                {/* Account Type Selection */}
                                <Form.Item
                                    name="accountType"
                                    rules={[{ required: true, message: t('auth.register.accountTypeRequired') }]}
                                    style={{ marginBottom: 24 }}
                                >
                                    <Text
                                        style={{
                                            color: palette.textPrimary,
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            display: 'block',
                                            marginBottom: '12px',
                                        }}
                                    >
                                        {t('auth.register.accountType')} <span style={{ color: 'red' }}>*</span>
                                    </Text>
                                    <Radio.Group
                                        onChange={(e) => { form.setFieldsValue({ accountType: e.target.value }); setAccountType(e.target.value); }}
                                        style={{ width: '100%' }}
                                    >
                                        <div style={{ display: 'flex', gap: 16, width: '100%' }}>
                                            <Radio.Button
                                                value="parent"
                                                style={{
                                                    flex: 1,
                                                    height: 100,
                                                    borderRadius: 12,
                                                    border: `2px solid ${palette.border}`,
                                                    background: palette.surface,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 0.3s ease',
                                                    padding: 0,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        width: '100%',
                                                        height: '100%',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <UserOutlined
                                                        style={{
                                                            fontSize: 32,
                                                            color: palette.textPrimary,
                                                        }}
                                                    />
                                                    <span
                                                        style={{
                                                            fontSize: 15,
                                                            fontWeight: 500,
                                                            color: palette.textPrimary,
                                                        }}
                                                    >
                                                        {t('auth.register.parent')}
                                                    </span>
                                                </div>
                                            </Radio.Button>
                                            <Radio.Button
                                                value="organization"
                                                style={{
                                                    flex: 1,
                                                    height: 100,
                                                    borderRadius: 12,
                                                    border: `2px solid ${palette.border}`,
                                                    background: palette.surface,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 0.3s ease',
                                                    padding: 0,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        width: '100%',
                                                        height: '100%',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <BankOutlined
                                                        style={{
                                                            fontSize: 32,
                                                            color: palette.textPrimary,
                                                        }}
                                                    />
                                                    <span
                                                        style={{
                                                            fontSize: 15,
                                                            fontWeight: 500,
                                                            color: palette.textPrimary,
                                                        }}
                                                    >
                                                        {t('auth.register.organization')}
                                                    </span>
                                                </div>
                                            </Radio.Button>
                                        </div>
                                    </Radio.Group>
                                </Form.Item>
                                <style>{radioStyles}</style>

                                {/* Conditional Forms */}
                                {accountType && (
                                    <>
                                        <Row gutter={[16, 16]}>
                                            {/* Personal Info for Parent */}
                                            {accountType === 'parent' && (
                                                <Col xs={24}>
                                                    <PersonalInfoForm />
                                                </Col>
                                            )}

                                            {/* Business Info for Organization */}
                                            {accountType === 'organization' && (
                                                <Col xs={24}>
                                                    <BusinessInfoForm />
                                                </Col>
                                            )}

                                            {/* Email and Password Section */}
                                            <Col xs={24}>
                                                <Card
                                                    title={
                                                        <Text
                                                            style={{
                                                                color: palette.textPrimary,
                                                                fontSize: '16px',
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {t('auth.register.loginInfoTitle')}
                                                        </Text>
                                                    }
                                                    bordered={false}
                                                    style={{
                                                        borderRadius: '16px',
                                                        border: `1px solid ${palette.border}`,
                                                        marginBottom: 16,
                                                    }}
                                                    bodyStyle={{ padding: '24px' }}
                                                >
                                                    <Form.Item
                                                        label={
                                                            <Text
                                                                style={{
                                                                    color: palette.textPrimary,
                                                                    fontSize: '14px',
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                {t('auth.register.loginLabel')}
                                                            </Text>
                                                        }
                                                        style={{ marginBottom: 18 }}
                                                    >
                                                        <Space.Compact style={{ width: '100%' }}>
                                                            <Form.Item
                                                                name="login"
                                                                noStyle
                                                                rules={[
                                                                    { required: true, message: t('auth.register.loginRequired') },
                                                                    { min: 2, message: t('auth.register.loginMinLength') },
                                                                    {
                                                                        validator: (_, value) => {
                                                                            if (value && /[\u0600-\u06FF]/.test(value)) {
                                                                                return Promise.reject(new Error(t('auth.validation.noArabicCharacters')));
                                                                            }
                                                                            return Promise.resolve();
                                                                        },
                                                                    },
                                                                ]}
                                                                getValueFromEvent={(e) => {
                                                                    const filtered = filterArabicCharacters(e.target.value);
                                                                    if (filtered !== e.target.value) {
                                                                        showArabicError();
                                                                    }
                                                                    return filtered;
                                                                }}
                                                                normalize={(value) => filterArabicCharacters(value || '')}
                                                            >
                                                                <Input
                                                                    prefix={<UserOutlined style={{ color: palette.textMuted }} />}
                                                                    placeholder={t('auth.register.loginPlaceholder')}
                                                                    size="large"
                                                                    disabled
                                                                    bordered={false}
                                                                    style={{
                                                                        borderRadius: '12px 0 0 12px',
                                                                        padding: '12px 16px',
                                                                        background: palette.background,
                                                                        border: `1px solid ${palette.border}`,
                                                                        fontSize: '15px',
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                            <Button
                                                                type="primary"
                                                                onClick={generateLogin}
                                                                loading={generatingLogin}
                                                                size="large"
                                                                style={{
                                                                    borderRadius: '0 12px 12px 0',
                                                                    height: 'auto',
                                                                    border: 'none',
                                                                    background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {t('auth.register.generateLogin')}
                                                            </Button>
                                                        </Space.Compact>
                                                    </Form.Item>
                                                    <Form.Item
                                                        name="email"
                                                        label={
                                                            <Text
                                                                style={{
                                                                    color: palette.textPrimary,
                                                                    fontSize: '14px',
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                {t('auth.register.email')}
                                                            </Text>
                                                        }
                                                        rules={[
                                                            { required: true, message: t('auth.register.emailRequired') },
                                                            { type: 'email', message: t('auth.register.emailInvalid') },
                                                        ]}
                                                        style={{ marginBottom: 18 }}
                                                    >
                                                        <Input
                                                            prefix={<MailOutlined style={{ color: palette.textMuted }} />}
                                                            placeholder={t('auth.register.emailPlaceholder')}
                                                            size="large"
                                                            bordered={false}
                                                            style={{
                                                                borderRadius: '12px',
                                                                padding: '12px 16px',
                                                                background: palette.background,
                                                                border: `1px solid ${palette.border}`,
                                                                fontSize: '15px',
                                                            }}
                                                        />
                                                    </Form.Item>

                                                    <Form.Item
                                                        name="password"
                                                        label={
                                                            <Text
                                                                style={{
                                                                    color: palette.textPrimary,
                                                                    fontSize: '14px',
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                {t('auth.register.password')}
                                                            </Text>
                                                        }
                                                        rules={[
                                                            { required: true, message: t('auth.register.passwordRequired') },
                                                            { min: 6, message: t('auth.register.passwordMinLength') },
                                                        ]}
                                                        style={{ marginBottom: 18 }}
                                                    >
                                                        <Input.Password
                                                            prefix={<LockOutlined style={{ color: palette.textMuted }} />}
                                                            placeholder={t('auth.register.passwordPlaceholder')}
                                                            size="large"
                                                            bordered={false}
                                                            style={{
                                                                borderRadius: '12px',
                                                                padding: '12px 16px',
                                                                background: palette.background,
                                                                border: `1px solid ${palette.border}`,
                                                                fontSize: '15px',
                                                            }}
                                                        />
                                                    </Form.Item>

                                                    <Form.Item
                                                        name="confirmPassword"
                                                        label={
                                                            <Text
                                                                style={{
                                                                    color: palette.textPrimary,
                                                                    fontSize: '14px',
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                {t('auth.register.confirmPassword')}
                                                            </Text>
                                                        }
                                                        dependencies={['password']}
                                                        rules={[
                                                            { required: true, message: t('auth.register.confirmPasswordRequired') },
                                                            ({ getFieldValue }) => ({
                                                                validator(_, value) {
                                                                    if (!value || getFieldValue('password') === value) {
                                                                        return Promise.resolve();
                                                                    }
                                                                    return Promise.reject(new Error(t('auth.register.passwordMismatch')));
                                                                },
                                                            }),
                                                        ]}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        <Input.Password
                                                            prefix={<LockOutlined style={{ color: palette.textMuted }} />}
                                                            placeholder={t('auth.register.confirmPasswordPlaceholder')}
                                                            size="large"
                                                            bordered={false}
                                                            style={{
                                                                borderRadius: '12px',
                                                                padding: '12px 16px',
                                                                background: palette.background,
                                                                border: `1px solid ${palette.border}`,
                                                                fontSize: '15px',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Card>
                                            </Col>

                                            {/* Contacts Section */}
                                            <Col xs={24}>
                                                <ContactsManager />
                                            </Col>
                                        </Row>

                                        {/* Country Selection */}
                                        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                                            <Col xs={24}>
                                                <Card
                                                    bordered={false}
                                                    style={{
                                                        borderRadius: '16px',
                                                        border: `1px solid ${palette.border}`,
                                                    }}
                                                    bodyStyle={{ padding: '24px' }}
                                                >
                                                    <Form.Item
                                                        name="country"
                                                        label={
                                                            <Text
                                                                style={{
                                                                    color: palette.textPrimary,
                                                                    fontSize: '14px',
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                {t('auth.register.country')}
                                                            </Text>
                                                        }
                                                        rules={[]}
                                                        style={{ marginBottom: 8 }}
                                                    >
                                                        <Select
                                                            placeholder={t('auth.register.countryPlaceholder')}
                                                            showSearch
                                                            optionFilterProp="label"
                                                            size="large"
                                                            style={{
                                                                width: '100%',
                                                                borderRadius: '12px',
                                                            }}
                                                            loading={!isReferentialLoaded}
                                                            options={countries.map((c) => ({
                                                                value: c.code,
                                                                label: c.name,
                                                            }))}
                                                        />
                                                    </Form.Item>
                                                </Card>
                                            </Col>
                                        </Row>

                                        <Form.Item style={{ marginTop: 24, marginBottom: 16 }}>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                size="large"
                                                block
                                                loading={isLoading}
                                                style={{
                                                    borderRadius: '12px',
                                                    height: '52px',
                                                    border: 'none',
                                                    background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                                                    boxShadow: `0 4px 12px rgba(45, 156, 201, 0.3)`,
                                                    fontWeight: 600,
                                                    fontSize: '16px',
                                                    transition: 'all 0.3s',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '1px',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.boxShadow = `0 6px 20px rgba(45, 156, 201, 0.4)`;
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.boxShadow = `0 4px 12px rgba(45, 156, 201, 0.3)`;
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                }}
                                            >
                                                {isLoading ? t('auth.register.submitting') : t('auth.register.submit')}
                                            </Button>
                                        </Form.Item>
                                    </>
                                )}
                            </Form>
                        </div>

                        {/* Login Link */}
                        <div
                            style={{
                                textAlign: 'center',
                                paddingTop: '24px',
                                borderTop: `1px solid ${palette.border}`,
                                flexShrink: 0,
                            }}
                        >
                            <Text style={{ color: palette.textSecondary, fontSize: '14px' }}>
                                {t('auth.register.alreadyHaveAccount')}{' '}
                                <a
                                    href="/login"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate('/login');
                                    }}
                                    style={{
                                        color: palette.primary,
                                        fontWeight: 600,
                                        textDecoration: 'none',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.textDecoration = 'underline';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.textDecoration = 'none';
                                    }}
                                >
                                    {t('auth.login.submit')}
                                </a>
                            </Text>
                        </div>
                    </div>
                </div>
            </Content>

            <Footer
                style={{
                    background: 'transparent',
                    padding: isCompact ? '16px 12px max(16px, env(safe-area-inset-bottom))' : 24,
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                <Text
                    style={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: isCompact ? 12 : 13,
                    }}
                >
                    {t('auth.login.footer', { year: new Date().getFullYear(), version: appVersion || 'dev' })}
                </Text>
            </Footer>
        </Layout>
    );
};

export default Register;

